import { FC, useState, useRef, useEffect } from 'react';
import ReusableModal from '../common/Modal';
import { Box, Button, FormControl, FormLabel, TextField } from '@mui/material';
import { commonStyles } from '../../utils/styles/inputs';

interface CommitMessageModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (commitMessage: string) => void;
  isLoading?: boolean;
}

const CommitMessageModal: FC<CommitMessageModalProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading = false,
}) => {
  const [commitMessage, setCommitMessage] = useState('');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const handleSubmit = () => {
    if (commitMessage.trim()) {
      onConfirm(commitMessage.trim());
      setCommitMessage(''); // Reset after submit
    }
  };

  const handleClose = () => {
    setCommitMessage(''); // Reset on close
    onClose();
  };

  useEffect(() => {
    if (open) {
      const id = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(id);
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <ReusableModal
      open={open}
      onClose={handleClose}
      title="Commit Changes"
      width={500}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl fullWidth>
          <FormLabel sx={commonStyles.formLabel}>Commit Message</FormLabel>
          <TextField
            id="commit-message"
            name="commitMessage"
            placeholder="Describe the changes you made to the system prompt..."
            required
            fullWidth
            variant="outlined"
            multiline
            minRows={3}
            maxRows={6}
            inputRef={inputRef}
            onKeyDown={handleKeyDown}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
                borderRadius: '6px',
                '& fieldset': {
                  borderColor: '#E0E0E0',
                },
              },
            }}
            value={commitMessage}
            onChange={e => setCommitMessage(e.target.value)}
          />
        </FormControl>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mt: 1,
            pt: 2,
            borderTop: '1px solid #DCE0E7',
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              flex: 1,
              color: 'custom.textBlue',
              fontWeight: 500,
              fontSize: '14px',
            }}
            disabled={isLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!commitMessage.trim() || isLoading}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              flex: 1,
              boxShadow: 'none',
            }}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>
    </ReusableModal>
  );
};

export default CommitMessageModal;
